/**
 * Video upload utilities — presigned S3 URL + chunked fallback
 */

const CHUNK_SIZE = 384 * 1024;

export const uploadViaPresignedUrl = async ({ apiBase, token, file, workoutId, onProgress, abortRef }) => {
  onProgress(2);
  const presignResp = await fetch(`${apiBase}/api/admin/videos/upload/presign`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename: file.name, content_type: file.type || 'video/mp4' }),
  });
  if (!presignResp.ok) {
    const errText = await presignResp.text();
    throw new Error(`Server error: ${presignResp.status} - ${errText}`);
  }
  const { upload_url, public_url } = await presignResp.json();
  onProgress(5);

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    abortRef.current = { abort: () => xhr.abort() };

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round(5 + (e.loaded / e.total) * 90));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed with status ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload. Check your connection.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out. Try again on a stable connection.'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));

    xhr.open('PUT', upload_url);
    xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
    xhr.timeout = 0;
    xhr.send(file);
  });

  onProgress(96);

  const confirmResp = await fetch(`${apiBase}/api/admin/videos/upload/confirm`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      public_url, filename: file.name,
      workout_id: workoutId || null,
      size_bytes: file.size,
    }),
  });
  if (!confirmResp.ok) throw new Error('Failed to confirm upload');
  onProgress(100);
  return await confirmResp.json();
};

export const uploadViaChunks = async ({ apiBase, token, file, workoutId, onProgress, abortRef }) => {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  onProgress(2);

  const initResp = await fetch(`${apiBase}/api/admin/videos/upload/init`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename: file.name,
      total_size: file.size,
      total_chunks: totalChunks,
      workout_id: workoutId || null,
    }),
  });
  if (!initResp.ok) throw new Error(`Init failed: ${initResp.status}`);
  const { upload_id } = await initResp.json();

  for (let i = 0; i < totalChunks; i++) {
    if (abortRef.current?.aborted) throw new Error('Upload cancelled');

    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    const base64Data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = () => reject(new Error('Failed to read file chunk'));
      reader.readAsDataURL(chunk);
    });

    let success = false;
    for (let attempt = 0; attempt < 5 && !success; attempt++) {
      try {
        const resp = await fetch(`${apiBase}/api/admin/videos/upload/chunk`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ upload_id, chunk_index: i, data: base64Data }),
        });
        if (!resp.ok) throw new Error(`Status ${resp.status}`);
        success = true;
      } catch (err) {
        if (attempt === 4) throw new Error(`Failed at ${Math.round((i / totalChunks) * 100)}%: ${err.message}`);
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
    onProgress(Math.round(2 + ((i + 1) / totalChunks) * 93));
  }

  const completeResp = await fetch(`${apiBase}/api/admin/videos/upload/complete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ upload_id }),
  });
  if (!completeResp.ok) throw new Error('Failed to finalize upload');
  onProgress(100);
  return await completeResp.json();
};
